import { AlertTriangle, Shield, Lock, Unlock, FileText, Folder, HardDrive, Download, Eye, EyeOff } from 'lucide-react';
import React, { useState, useEffect } from 'react';

const EnhancedRegistryEditorContent = ({ scenario, onComplete }) => {
  const [currentPath, setCurrentPath] = useState('HKEY_CURRENT_USER\\Software\\Microsoft\\Windows\\CurrentVersion\\Run');
  const [registryEntries, setRegistryEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showProperties, setShowProperties] = useState(false);

  useEffect(() => {
    // Initialize registry entries based on scenario
    const baseEntries = [
      {
        id: 'windows-defender',
        name: 'Windows Defender',
        value: 'C:\\Program Files\\Windows Defender\\MSASCui.exe',
        type: 'REG_SZ',
        suspicious: false
      },
      {
        id: 'windows-update',
        name: 'Windows Update',
        value: 'C:\\Windows\\System32\\wuauclt.exe',
        type: 'REG_SZ',
        suspicious: false
      }
    ];

    // Add scenario-specific malicious entries
    if (scenario?.maliciousEntries) {
      setRegistryEntries([...baseEntries, ...scenario.maliciousEntries]);
    } else {
      // Default malicious entry for demonstration
      setRegistryEntries([
        ...baseEntries,
        {
          id: 'malicious-update',
          name: 'UpdateSvc',
          value: 'mshta.exe javascript:...',
          type: 'REG_SZ',
          suspicious: true,
          description: 'Suspicious registry entry using mshta.exe to execute JavaScript'
        }
      ]);
    }
  }, [scenario]);

  const handleDeleteEntry = (entryId) => {
    setRegistryEntries(prev => prev.filter(entry => entry.id !== entryId));
    setSelectedEntry(null);
    setShowProperties(false);
    
    // Check if all malicious entries are removed
    const remainingMalicious = registryEntries.filter(entry => entry.suspicious && entry.id !== entryId);
    if (remainingMalicious.length === 0) {
      onComplete({
        success: true,
        message: 'All malicious registry entries removed.',
        correct: 1,
        total: 1,
        score: 100
      });
    }
  };

  const navigateToPath = (path) => {
    setCurrentPath(path);
    setSelectedEntry(null);
    setShowProperties(false);
  };

  return (
    <div className="h-full flex flex-col bg-white text-black">
      {/* Address Bar */}
      <div className="flex items-center p-2 border-b border-gray-200 bg-gray-50">
        <span className="text-sm font-medium text-gray-700 mr-2">Address:</span>
        <input
          value={currentPath}
          onChange={(e) => setCurrentPath(e.target.value)}
          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
        />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Registry Tree */}
        <div className="w-64 border-r border-gray-200 p-2 overflow-y-auto bg-gray-50">
          <div className="space-y-1">
            <div className="font-medium text-sm text-gray-700 mb-2">Registry</div>
            <div className="pl-2">
              <div className="text-sm text-blue-600 cursor-pointer hover:underline" onClick={() => navigateToPath('HKEY_CURRENT_USER\\Software\\Microsoft\\Windows\\CurrentVersion\\Run')}>
                HKEY_CURRENT_USER
              </div>
              <div className="pl-4">
                <div className="text-sm text-blue-600 cursor-pointer hover:underline" onClick={() => navigateToPath('HKEY_CURRENT_USER\\Software\\Microsoft\\Windows\\CurrentVersion\\Run')}>
                  Software
                </div>
                <div className="pl-4">
                  <div className="text-sm text-blue-600 cursor-pointer hover:underline" onClick={() => navigateToPath('HKEY_CURRENT_USER\\Software\\Microsoft\\Windows\\CurrentVersion\\Run')}>
                    Microsoft
                  </div>
                  <div className="pl-4">
                    <div className="text-sm text-blue-600 cursor-pointer hover:underline" onClick={() => navigateToPath('HKEY_CURRENT_USER\\Software\\Microsoft\\Windows\\CurrentVersion\\Run')}>
                      Windows
                    </div>
                    <div className="pl-4">
                      <div className="text-sm text-blue-600 cursor-pointer hover:underline" onClick={() => navigateToPath('HKEY_CURRENT_USER\\Software\\Microsoft\\Windows\\CurrentVersion\\Run')}>
                        CurrentVersion
                      </div>
                      <div className="pl-4">
                        <div className="text-sm text-blue-600 cursor-pointer hover:underline font-medium" onClick={() => navigateToPath('HKEY_CURRENT_USER\\Software\\Microsoft\\Windows\\CurrentVersion\\Run')}>
                          Run
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Registry Entries */}
        <div className="flex-1 flex flex-col">
          <div className="p-2 border-b border-gray-200 bg-gray-50">
            <div className="text-sm font-medium text-gray-700">Registry Entries</div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="text-left p-2 font-medium text-gray-700">Name</th>
                  <th className="text-left p-2 font-medium text-gray-700">Type</th>
                  <th className="text-left p-2 font-medium text-gray-700">Data</th>
                </tr>
              </thead>
              <tbody>
                {registryEntries.map((entry) => (
                  <tr
                    key={entry.id}
                    onClick={() => {
                      setSelectedEntry(entry);
                      setShowProperties(true);
                    }}
                    className={`border-b border-gray-200 cursor-pointer hover:bg-blue-50 ${
                      entry.suspicious ? 'bg-red-50 hover:bg-red-100' : ''
                    }`}
                  >
                    <td className="p-2">
                      <div className="flex items-center">
                        {entry.suspicious && (
                          <AlertTriangle size={14} className="mr-2 text-red-500 flex-shrink-0" />
                        )}
                        <span className={entry.suspicious ? 'text-red-600 font-medium' : ''}>
                          {entry.name}
                        </span>
                      </div>
                    </td>
                    <td className="p-2 text-gray-600">{entry.type}</td>
                    <td className="p-2 text-gray-600 font-mono text-xs truncate max-w-xs" title={entry.value}>
                      {entry.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Properties Panel */}
        {showProperties && selectedEntry && (
          <div className="w-80 border-l border-gray-200 p-4 bg-gray-50">
            <h3 className="font-semibold text-gray-800 mb-4">Properties</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Name:</label>
                <div className="text-sm text-gray-900 mt-1">{selectedEntry.name}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Type:</label>
                <div className="text-sm text-gray-900 mt-1">{selectedEntry.type}</div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Value:</label>
                <div className="text-sm text-gray-900 mt-1 font-mono break-all">{selectedEntry.value}</div>
              </div>
              {selectedEntry.suspicious && (
                <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded">
                  <div className="flex items-center mb-2">
                    <AlertTriangle size={16} className="text-red-600 mr-2" />
                    <span className="font-bold text-red-800">Suspicious Entry</span>
                  </div>
                  <p className="text-red-700 text-sm">
                    {selectedEntry.description || 'This registry entry exhibits suspicious characteristics.'}
                  </p>
                </div>
              )}
            </div>
            <div className="mt-6">
              {selectedEntry.suspicious && (
                <button
                  onClick={() => handleDeleteEntry(selectedEntry.id)}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center space-x-2"
                >
                  <Shield size={16} />
                  <span>Delete Entry</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedRegistryEditorContent;